import React, { createContext, useContext, useRef, useCallback } from 'react';
import { devLog } from '../lib/log';

const DataCacheContext = createContext({});

export const useCache = () => useContext(DataCacheContext);

/**
 * Hook stale-while-revalidate per dati cached.
 * - Se cache vuota: loading=true, fetch, loading=false
 * - Se cache stale: ritorna dati subito (loading=false), fetch in background, aggiorna
 * - Se cache fresca: ritorna dati, nessun fetch
 */
export const useDataCache = (key, fetchFn, ttlMs = 60000) => {
  const { getCache, setCache } = useCache();
  const [data, setData] = React.useState(() => getCache(key)?.data ?? null);
  const [loading, setLoading] = React.useState(() => getCache(key)?.data == null);
  const fetchingRef = React.useRef(false);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  React.useEffect(() => {
    if (!key || !fetchFn) return;

    const cached = getCache(key);
    const now = Date.now();

    if (cached?.data != null) {
      // Cache exists — show it immediately
      setData(cached.data);
      setLoading(false);

      // Check if stale
      if (now - cached.timestamp > ttlMs) {
        // Stale — revalidate in background
        revalidate();
      }
    } else {
      // No cache — show loading and fetch
      setLoading(true);
      revalidate();
    }
  }, [key]); // Re-run when key changes (e.g., weekOffset)

  const revalidate = useCallback(async () => {
    if (fetchingRef.current) {
      /*#__PURE__*/ devLog('[CACHE] Already fetching ', key, ', skipping');
      return;
    }
    fetchingRef.current = true;
    /*#__PURE__*/ devLog('[CACHE] Starting fetch for ', key);

    try {
      const result = await fetchFn();
      /*#__PURE__*/ devLog('[CACHE] Fetch complete for ', key, ', result:', result ? 'OK' : 'null');
      if (mountedRef.current) {
        setData(result);
        setCache(key, result);
        setLoading(false);
      }
    } catch (err) {
      console.error(`[CACHE] Error fetching ${key}:`, err.message || err);
      if (mountedRef.current) {
        setLoading(false);
      }
    } finally {
      fetchingRef.current = false;
    }
  }, [key, fetchFn, setCache]);

  const refresh = useCallback(() => {
    setLoading(data == null);
    revalidate();
  }, [data, revalidate]);

  return { data, loading, refresh };
};

export const DataCacheProvider = ({ children }) => {
  const cacheRef = useRef(new Map());

  const getCache = useCallback((key) => {
    return cacheRef.current.get(key) || null;
  }, []);

  const setCache = useCallback((key, data) => {
    cacheRef.current.set(key, { data, timestamp: Date.now() });
  }, []);

  const invalidate = useCallback((keyOrPattern) => {
    if (typeof keyOrPattern === 'string' && keyOrPattern.includes('*')) {
      // Pattern matching: "athlete-*-abc123" → invalidate all matching
      const prefix = keyOrPattern.split('*')[0];
      for (const k of cacheRef.current.keys()) {
        if (k.startsWith(prefix)) {
          cacheRef.current.delete(k);
        }
      }
    } else {
      cacheRef.current.delete(keyOrPattern);
    }
  }, []);

  const invalidateAll = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const value = { getCache, setCache, invalidate, invalidateAll };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};
